'use client';

import { useCheckout } from '../hooks/useCheckout';
import type { Interval, Plan, Tier } from '../types/billing.types';

interface PlanCardProps {
  plan: Plan;
  interval: Interval;
  stripeEnabled: boolean;
  cryptoEnabled: boolean;
}

const HIGHLIGHTED_TIER: Tier = 'pro';

export function PlanCard({ plan, interval, stripeEnabled, cryptoEnabled }: PlanCardProps) {
  const { startCheckout, isLoading } = useCheckout();
  const intervalData = interval === 'monthly' ? plan.monthly : plan.annual;
  const highlighted = plan.tier === HIGHLIGHTED_TIER;

  const isStarterFreeBeta = plan.tier === 'starter';
  // The starter tier is free during closed beta; once paid plans go live the
  // backend just flips it to `available: true` with a price.
  const canCheckout =
    !isStarterFreeBeta &&
    plan.tier !== 'free' &&
    intervalData.available &&
    (stripeEnabled || cryptoEnabled);

  return (
    <div
      className={`flex flex-col rounded-lg border p-6 ${
        highlighted ? 'border-coral bg-coral/5' : 'border-border'
      }`}
    >
      <h3 className="font-serif text-2xl">{plan.name}</h3>
      <Price interval={interval} amount={intervalData.amount_usd} />
      <ul className="mt-4 flex-1 space-y-1 text-sm">
        <li>· {formatLimit(plan.coach_messages_per_month, 'coach messages / mo')}</li>
        <li>· {formatLimit(plan.connected_accounts, 'connected accounts')}</li>
        <li>· {formatJournalHistory(plan.journal_history_days)}</li>
      </ul>
      <p className="mt-3 text-sm text-muted-foreground">{plan.description}</p>

      <div className="mt-4 flex flex-col gap-2">
        {canCheckout ? (
          <>
            {stripeEnabled && (
              <button
                type="button"
                onClick={() =>
                  startCheckout({
                    tier: plan.tier as Exclude<Tier, 'free' | 'starter'>,
                    interval,
                    provider: 'stripe',
                  })
                }
                disabled={isLoading}
                className="rounded-md bg-coral px-4 py-2 text-sm font-medium text-paper disabled:opacity-60"
              >
                {isLoading ? 'Starting…' : 'Subscribe with card'}
              </button>
            )}
            {cryptoEnabled && (
              <button
                type="button"
                onClick={() =>
                  startCheckout({
                    tier: plan.tier as Exclude<Tier, 'free' | 'starter'>,
                    interval,
                    provider: 'nowpayments',
                  })
                }
                disabled={isLoading}
                className="rounded-md border border-border px-4 py-2 text-sm font-medium disabled:opacity-60"
              >
                Pay with crypto
              </button>
            )}
          </>
        ) : (
          <DisabledCta plan={plan} interval={interval} />
        )}
      </div>
    </div>
  );
}

function Price({ interval, amount }: { interval: Interval; amount: number | null }) {
  if (amount === null || amount === 0) {
    return (
      <div className="mt-2 font-mono text-3xl text-muted-foreground">—</div>
    );
  }
  return (
    <div className="mt-2 flex items-baseline gap-1">
      <span className="font-mono text-3xl">${amount}</span>
      <span className="text-sm text-muted-foreground">
        {interval === 'monthly' ? '/mo' : '/yr'}
      </span>
    </div>
  );
}

function DisabledCta({ plan, interval }: { plan: Plan; interval: Interval }) {
  if (plan.tier === 'starter') {
    return (
      <span className="rounded-md border border-border px-4 py-2 text-center text-xs text-muted-foreground">
        Free during closed beta
      </span>
    );
  }
  const intervalData = interval === 'monthly' ? plan.monthly : plan.annual;
  if (!intervalData.available) {
    return (
      <span className="rounded-md border border-border px-4 py-2 text-center text-xs text-muted-foreground">
        Pricing not configured yet
      </span>
    );
  }
  return (
    <span className="rounded-md border border-border px-4 py-2 text-center text-xs text-muted-foreground">
      Payments coming soon
    </span>
  );
}

function formatLimit(value: number, unit: string): string {
  if (value === -1) return `Unlimited ${unit}`;
  return `${value} ${unit}`;
}

function formatJournalHistory(days: number): string {
  if (days === -1) return 'Unlimited journal history';
  return `${days}-day journal history`;
}
